<template>
  <div class="coupon-property-pool-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('couponPropertyPoolManage.page.title')" :subtitle="$t('couponPropertyPoolManage.page.subtitle')" />

    <!-- (2) SearchPanel：筛选字段与含义保持不变，仅重排栅格并统一动作区 -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.cppId" clearable
            :placeholder="$t('couponPropertyPoolManage.search.cppId')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.couponName" clearable
            :placeholder="$t('couponPropertyPoolManage.search.couponName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.fromType" :placeholder="$t('couponPropertyPoolManage.search.fromType')" clearable
            class="w-full">
            <el-option v-for="item in fromTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.toType" :placeholder="$t('couponPropertyPoolManage.search.toType')" clearable
            class="w-full">
            <el-option v-for="item in toTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('couponPropertyPoolManage.list.title') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAdd">
            {{ $t('couponPropertyPoolManage.list.add') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('couponPropertyPoolManage.page.empty')" @retry="getList">
        <el-table-column prop="cppId" :label="$t('couponPropertyPoolManage.table.cppId')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.cppId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="couponName" :label="$t('couponPropertyPoolManage.table.couponName')" align="left"
          min-width="160" show-overflow-tooltip />
        <el-table-column :label="$t('couponPropertyPoolManage.table.fromType')" align="center" width="140">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveFromType(scope.row)" :label="scope.row.fromTypeName || '-'" />
          </template>
        </el-table-column>
        <el-table-column prop="toTypeName" :label="$t('couponPropertyPoolManage.table.toType')" align="center" width="110" />
        <el-table-column :label="$t('couponPropertyPoolManage.table.stock')" align="right" width="100">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatNumber(scope.row.stock, 0) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('couponPropertyPoolManage.table.validityDay')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatNumber(scope.row.validityDay, 0) }}</span>
            <span class="ds-cell-sub">{{ $t('couponPropertyPoolManage.table.day') }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('couponPropertyPoolManage.table.createTime')" align="center"
          width="160" />
        <el-table-column :label="$t('common.operation')" align="center" width="220" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="scope.row.stock > 0" size="mini" type="text" @click="handleGift(scope.row)">
              {{ $t('couponPropertyPoolManage.table.gift') }}
            </el-button>
            <el-button v-if="scope.row.fromType === '2002'" size="mini" type="text" @click="handleEdit(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button v-if="scope.row.fromType === '2002'" size="mini" type="text" class="ds-btn-danger"
              @click="handleDelete(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 组件 -->
    <add-coupon-property-pool ref="addCouponPropertyPool" @success="handleSuccess" />
    <edit-coupon-property-pool ref="editCouponPropertyPool" @success="handleSuccess" />
    <gift-coupon-property-pool ref="giftCouponPropertyPool" @success="handleSuccess" />
    <delete-coupon-property-pool ref="deleteCouponPropertyPool" @success="handleSuccess" />
  </div>
</template>

<script>
import { listCouponPropertyPool } from '@/api/scm/couponPropertyPoolManageApi'
import AddCouponPropertyPool from '@/components/scm/addCouponPropertyPool'
import EditCouponPropertyPool from '@/components/scm/editCouponPropertyPool'
import GiftCouponPropertyPool from '@/components/scm/giftCouponPropertyPool'
import DeleteCouponPropertyPool from '@/components/scm/deleteCouponPropertyPool'
import { getCommunityId } from '@/api/community/communityApi'
import { formatNumber } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CouponPropertyPoolManageList',
  components: {
    AddCouponPropertyPool,
    EditCouponPropertyPool,
    GiftCouponPropertyPool,
    DeleteCouponPropertyPool,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        cppId: '',
        couponName: '',
        fromType: '',
        toType: '',
        communityId: getCommunityId()
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      fromTypeOptions: [
        { value: '1001', label: this.$t('couponPropertyPoolManage.fromType.market') },
        { value: '2002', label: this.$t('couponPropertyPoolManage.fromType.self') }
      ],
      toTypeOptions: [
        { value: '1001', label: this.$t('couponPropertyPoolManage.toType.shopping') },
        { value: '3003', label: this.$t('couponPropertyPoolManage.toType.repair') },
        { value: '4004', label: this.$t('couponPropertyPoolManage.toType.parking') }
      ]
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchForm,
          page: this.page.current,
          row: this.page.size
        }
        const res = await listCouponPropertyPool(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况。
        this.tableData = Array.isArray(res.data) ? res.data : []
        this.page.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('couponPropertyPoolManage.page.loadFailed')
        console.error('Failed to load coupon property pool:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    /**
     * 重置筛选：仅清空可筛选字段，保留 communityId。
     */
    handleReset() {
      this.searchForm.cppId = ''
      this.searchForm.couponName = ''
      this.searchForm.fromType = ''
      this.searchForm.toType = ''
      this.handleSearch()
    },
    handleAdd() {
      this.$refs.addCouponPropertyPool.open()
    },
    handleEdit(row) {
      this.$refs.editCouponPropertyPool.open(row)
    },
    handleGift(row) {
      this.$refs.giftCouponPropertyPool.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteCouponPropertyPool.open(row)
    },
    handleShowDoc() {
      // 显示文档逻辑
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    },
    /**
     * 数量格式化（只改展示，不改原值）
     */
    formatNumber(value, decimals = 2) {
      return formatNumber(value, decimals)
    },
    /**
     * 优惠券来源方式语义类型（1001 市场购买 / 2002 自己制作）
     */
    resolveFromType(row) {
      if (!row) return 'muted'
      return row.fromType === '2002' ? 'primary' : 'info'
    }
  }
}
</script>

<style lang="scss" scoped>
.coupon-property-pool-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  margin-left: 2px;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
